import {
  Component,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { from, Observable, Subject, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { TableDetails } from '../../shared/tablefilters/table-details.model';
import { Column } from '../../shared/tablefilters/column.model';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import {
  CommonService,
  CustomFormatCurrencyPipe,
  Exception,
  ToasterService,
} from '@anddone/ui-utilities';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { DatePipe } from '@angular/common';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { MessageConstants } from '../../../../../constants/message.constant';
import { OperationTypeMapEnum } from '../../../../../enum/transaction-origin.enum';
import {
  RefundTransactionStatusMapEnum,
  TransactionStatusMapEnum,
} from '../../../../../enum/transaction-status-map.enum';
import { PaymentService } from '../../../../../services/payment.service';
import { FiltersComponent } from '../../shared/tablefilters/filters/filters.component';
import { TableComponent } from '../../shared/tablefilters/table/table.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { HttpErrorResponse } from '@angular/common/http';
import { AppSetting } from '../../../../../constants/appsetting.constant';

@Component({
  selector: 'app-find-payments',
  imports: [
    CustomFormatCurrencyPipe,
    TableComponent,
    FiltersComponent,
    TranslateModule,
    NgbTooltip,
  ],
  templateUrl: './find-payments.html',
  styleUrl: './find-payments.scss',
  providers: [DatePipe],
})
export class FindPayments implements OnInit, OnDestroy {
  //variables
  transactionList = [];
  isFirstLoad = true;

  /*** Local variables needed for table filter system ***/
  searchParamsData: any = {};
  totalTransactionCount: any = {};
  pager: any = {};
  tableActionSub!: Subscription;
  searchInputSub!: Subscription;
  @ViewChild('amountCell', { static: true })
  amountCell!: TemplateRef<any>;
  private destroy$ = new Subject<void>();

  /*** Defined table details & columns ****/
  tableDetails: TableDetails = {
    tableId: 'transaction-table',
    searchPlaceholder: 'Shopper Name, Merchant Reference, Transaction ID, DBA Name',
    allowRowClick: true,
    rowClickURI: '/ally/payments/details',
    rowClickExtraParams: [
      {
        param: 'webOrigin',
        key: 'transactionOrigin',
      },
      {
        param: 'transactionStatus',
        key: 'transactionStatus',
      },
    ],
    tableTitle: 'Most Recent Transactions',
    defaultItemPerPage: ValidationConstant.defaultPageSize,
    sendDefault90DayFilter: false,
    countTitle: 'Transactions',
    countNumber: signal(0),
    showExport: true,
    exportType: 'directDownload',
    userEmailId: '',
  };
  columns: Column[] = [
    {
      name: 'DBA Name',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'dbaName',
      requestKey: 'DBAName',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Shopper Name',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'customerName',
      requestKey: 'ShopperName',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Method',
      sortState: 'none',
      isSortable: true,
      display: 'method',
      responseKey: 'method',
      requestKey: 'PaymentMethod',
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'AMEX', key: 'amex', checked: false },
          { label: 'ACH US Dir...', key: 'web', checked: false },
          { label: 'VISA', key: 'visa', checked: false },
          { label: 'DINERS', key: 'diners', checked: false },
          { label: 'DISCOVER', key: 'discover', checked: false },
          { label: 'MASTERCARD', key: 'mastercard', checked: false },
        ],
      },
    },
    {
      name: 'ACH/Card #',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'cardOrACHNumber',
      requestKey: 'MaskAccount',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Merchant Ref',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'merchantReference',
      requestKey: 'MerchantReference',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Ref ID',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'refId',
      requestKey: 'customerName',
      // filter: {
      //   type: 'string',
      //   validation: ['required']
      // },
    },
    {
      name: 'Ref Value',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'refValue',
      requestKey: 'customerName',
      // filter: {
      //   type: 'string',
      //   validation: ['required']
      // },
    },
    {
      name: 'Transaction Id',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      displayOptions: ['addCopyButton'],
      responseKey: 'transactionId',
      requestKey: 'TransactionIds',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Date',
      sortState: 'asc',
      isSortable: true,
      display: 'date',
      responseKey: 'transactionDate',
      requestKey: 'transactionDate',
      filter: {
        type: 'date',
        dateFromRequestKey: 'StartDate',
        dateToRequestKey: 'EndDate',
      },
    },
    {
      name: 'Status',
      sortState: 'none',
      isSortable: true,
      display: 'status',
      responseKey: 'transactionStatus',
      requestKey: 'TransactionStatuses',
      hasNestedRow: true,
      statusColors: {
        Authorized: 'purple',
        Settled: 'green',
        Refunded: 'blue',
        Chargeback: 'yellow',
        Failed: 'red',
        Voided: 'red',
        Captured: 'blue',
        'Refund Attempted': 'yellow',
        'Refund in process': 'yellow',
        'Refund Settled': 'green',
        'Refund Authorized': 'purple',
        'Refunded Reversed': 'green',
        'Paid Refunded': 'blue',
        'Paid Refunded Reversed': 'yellow',
      },
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Authorized', key: 'Authorized', checked: false },
          { label: 'Settled', key: 'Success', checked: false },
          { label: 'Refunded', key: 'Refunded', checked: false },
          { label: 'Chargeback', key: 'Chargeback', checked: false },
          { label: 'Failed', key: 'Failed', checked: false },
          { label: 'Voided', key: 'Cancelled', checked: false },
          { label: 'Captured', key: 'Captured', checked: false },
          { label: 'Refund Attempted', key: 'RefundAttempt', checked: false },
        ],
      },
    },
    {
      name: 'Amount',
      sortState: 'none',
      isSortable: true,
      display: 'template',
      responseKey: 'captureAmount',
      requestKey: 'captureAmount',
      cellTemplate: this.amountCell,
      filter: {
        type: 'amount',
        validation: ['number'],
        exactRequestKey: 'amount',
        minRequestKey: 'fromAmount',
        maxRequestKey: 'toAmount',
        selectedAmounts: {
          exact: null,
          min: null,
          max: null,
        },
      },
    },
  ];
  loggedInUserData: any;
  nonDefaultAdminLoggedInUserData: any;
  userEmailId!: string;

  config = {
    searchInput: {
      required: { name: ValidationConstant.transaction.searchInput.name },
      minlength: {
        name: ValidationConstant.transaction.searchInput.name,
        min: ValidationConstant.transaction.searchInput.minLength.toString(),
      },
    },
  };

  public allyCommonService = inject(AllyCommonService);
  private commonService = inject(CommonService);
  private paymentService = inject(PaymentService);
  private toasterService = inject(ToasterService);
  private tableFilterService = inject(TableFilterService);
  private datePipe = inject(DatePipe);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loggedInUserData = this.allyCommonService.getLoggedInData() || '';
    this.nonDefaultAdminLoggedInUserData = this.allyCommonService.getNonDefaultLoggedInData() || '';
    this.userEmailId = this.nonDefaultAdminLoggedInUserData?.contact?.email
      ? this.nonDefaultAdminLoggedInUserData?.contact?.email
      : this.loggedInUserData?.contact?.email;
    this.tableDetails.userEmailId = this.userEmailId;

    /*** Table Filter subscriptions ***/
    this.tableFilterService.init(this.columns, this.tableDetails);

    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;
        /*** Assign the method that manages your API call here. ***/
        this.getTransactionsCount(this.pager.currentPage);
        this.findTransaction(this.pager.currentPage);
      });

    this.tableFilterService.registerSubscriptions(this.searchInputSub, this.tableActionSub);
    /*** End of Table filter subscription handling ***/
    const col = this.columns.find((c) => c.responseKey === 'captureAmount');
    if (col) col.cellTemplate = this.amountCell;
  }

  /* transaction api call function */
  findTransaction(pageNumber: any) {
    this.commonService.scrollTop('transactionTable');
    if (pageNumber <= 0) {
      return;
    }
    if (this.pager.totalPages > 0) {
      if (pageNumber > this.pager.totalPages) {
        return;
      }
    }

    this.allyCommonService.setIsLoader(true);
    /*** prepared the searchParamsData object to be sent to the API call ***/
    this.searchParamsData.TransactionsCount = false;
    this.searchParamsData.TransactionType = OperationTypeMapEnum.Sale;
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber);
    if (this.isFirstLoad) {
      this.searchParamsData.SortField = 'transactionDate';
      this.searchParamsData.Asc = false;
      this.isFirstLoad = false;
    }

    this.paymentService
      .getAllTransaction(this.searchParamsData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          if (res.data.length == 0) {
            this.transactionList = [];
            this.tableFilterService.setPager(res, pageNumber, this.pager);

            /*** Update the tableData toshow no results  ***/
            this.tableDetails.tableData = [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          } else {
            this.tableFilterService.setPager(res, pageNumber, this.pager);
            this.transactionList = res.data;
            this.tableDetails.tableData = this.transactionList || [];
            this.parseTableData(this.tableDetails?.tableData);

            /*** Set the table details to include data to be displayed and pagination data and send to tablefilter service */
            this.tableFilterService.setTableDetails(this.tableDetails);
          }
        },
        error: (e) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  /*** method to get transaction count */
  getTransactionsCount(pageNumber: any) {
    this.allyCommonService.setIsLoader(true);
    /*** prepared the searchParamsData object to be sent to the API call ***/
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber);
    this.searchParamsData.TransactionsCount = true;
    this.searchParamsData.TransactionType = OperationTypeMapEnum.Sale;
    this.searchParamsData.TotalRecordCount = 0;
    this.paymentService
      .getAllTransaction(this.searchParamsData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.totalTransactionCount = res.totalRowCount;
          this.tableDetails.countNumber?.set(this.totalTransactionCount);
          this.pager = this.commonService.setPager(res, pageNumber, this.pager);
        },
        error: (e: any) => {
          this.allyCommonService.setIsLoader(false);
          const _toastMessage = Exception.exceptionMessage(e);
        },
      });
  }

  /*** loop through each row and apply table specific requirements ***/
  parseTableData(tableData: any) {
    for (const row of tableData) {
      this.overrideTableDataValues(row);
      //TODO add actions
    }
  }

  /***  Optional table data customization. If you need to change the values to be displayed, you can do that here.  ***/
  overrideTableDataValues(row: any) {
    if (row.cardNumber) {
      row.cardOrACHNumber = row.cardNumber;
    } else {
      row.cardOrACHNumber = row.accountNumber;
    }
    if (row.operationType == 'Refund') {
      const key = row.transactionStatus as keyof typeof RefundTransactionStatusMapEnum;
      row.transactionStatus = RefundTransactionStatusMapEnum[key];
    } else {
      const key = row.transactionStatus as keyof typeof TransactionStatusMapEnum;
      row.transactionStatus = TransactionStatusMapEnum[key];
    }
    row.merchantReference = row.merchantReference
      ? row.merchantReference
      : MessageConstants.common.noData;
    row.id = row.transactionId;
    if (row.additionaFields) {
      const additionaFields = JSON.parse(row.additionaFields);

      for (const key in additionaFields) {
        const lowerKey = key.toLowerCase();

        if (additionaFields[key]?.length > 0 && lowerKey) {
          row.refId = lowerKey;
          row.refValue = additionaFields[key];
          break;
        }
      }
    } else {
      row.refId = MessageConstants.common.noData;
      row.refValue = MessageConstants.common.noData;
    }
    if (row.channelType == 'ACH') {
      row.customerName = row.accountHolderName;
      row.method = 'ACH US DIRECT DEBIT';
    } else {
      row.method = row.cardType;
      row.customerName = row.nameOnCheckOrCard;
    }
  }

  /*** method to toggle refund data on row click ***/
  toggleRefundData(row: any) {
    if (!row.expanded) {
      const _queryParams = {
        transactionId: row.transactionId,
        includeRefundTransactions: true,
        splits: true,
      };
      row.loading = true;
      this.paymentService.getTransactionDetails(row.transactionId, true).subscribe({
        next: (res: any) => {
          if (res.refundTransactions.data.length != 0) {
            row.nestedRows = res.refundTransactions.data;
            row.expanded = true;
            for (const nestedRow of row.nestedRows) {
              this.overrideTableDataValues(nestedRow);
              nestedRow.dbaName = row.dbaName;
            }

            /*** Set the table details to include data to be displayed and pagination data and send to tablefilter service */
            this.tableFilterService.setTableDetails(this.tableDetails);
            row.expanded = true;
            row.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: (e: any) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
          row.loading = false;
          row.expanded = false;
          this.cdr.detectChanges();
        },
      });
    } else {
      row.nestedRows = null;
      row.expanded = false;
      row.loading = false;
    }
  }

  export(fileType: string): void {
    if (this.totalTransactionCount == 0) {
      this.toasterService.showInfo(MessageConstants.payments.noRecordFound, 'full');
      return;
    }
    if (
      ((fileType == 'csv' && this.totalTransactionCount <= AppSetting.csvExportCountLimit) ||
        (fileType == 'pdf' && this.totalTransactionCount <= AppSetting.pdfExportCountLimit)) &&
      this.allyCommonService.calculateDateDiff(this.searchParamsData.StartDate) + 1 <=
        AppSetting.exportDaysLimitForScenarioOne
    ) {
      this.tableDetails.exportType = 'directExport';
      this.tableFilterService.setTableDetails(this.tableDetails);
      this.exportScenarioOne(fileType);
    } else {
      this.tableDetails.exportType = 'emailExport';
      this.tableFilterService.setTableDetails(this.tableDetails);
      this.exportScenarioTwo(fileType);
    }
  }

  exportScenarioOne(fileType: string) {
    this.searchParamsData.ExportToCsv = null;
    this.searchParamsData.ExportToPDF = null;
    let fileName = '';
    this.searchParamsData.includeRefundTransactions = null;

    if (fileType == 'csv') {
      fileName = 'Payments_Export_' + this.datePipe.transform(new Date(), 'MMddyy_hhmmss') + '.csv';
      this.searchParamsData.ExportToCsv = true;
      this.paymentService.exportReportCSV(this.searchParamsData).subscribe({
        next: (res: any) => {
          this.directDownloadReport(fileName, fileType, res);
        },
        error: (e) => {
          this.handleBlobError(e).subscribe({
            next: (errorAsJson: any) => {
              const toastMessage = Exception.exceptionMessage(errorAsJson);
              this.toasterService.showError(toastMessage.join(', '), 'full');
            },
            error: () => {
              this.toasterService.showError('Something went wrong', 'full');
            },
          });
        },
      });
    }
    if (fileType == 'pdf') {
      fileName = 'Payments_Export_' + this.datePipe.transform(new Date(), 'MMddyy_hhmmss') + '.pdf';
      this.searchParamsData.ExportToPDF = true;
      this.paymentService.exportReportPDF(this.searchParamsData).subscribe({
        next: (res: any) => {
          this.directDownloadReport(fileName, fileType, res);
        },
        error: (e) => {
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    }
  }

  exportScenarioTwo(fileType: string) {
    this.searchParamsData.ExportToCsv = null;
    this.searchParamsData.ExportToPDF = null;
    if (fileType == 'pdf') {
      this.searchParamsData.ExportToPDF = true;
    } else if (fileType == 'csv') {
      this.searchParamsData.ExportToCsv = true;
    }
    this.paymentService.emailExportReport(this.searchParamsData).subscribe({
      next: (_res: any) => {
        /* */
      },
      error: (e) => {
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  directDownloadReport(fileName: any, fileType: any, fileData: any) {
    if (fileType == 'csv') {
      if (!(fileData instanceof Blob)) {
        console.error('Invalid CSV data: expected Blob');
        return;
      }
      const url = window.URL.createObjectURL(fileData);
      const a = document.createElement('a');
      a.setAttribute('style', 'display:none;');
      document.body.appendChild(a);
      a.href = url;
      a.download = fileName;
      a.click();
      document.body.removeChild(a);
    }
    if (fileType == 'pdf') {
      const fileURL = fileData?.preSignedURL;
      const a = document.createElement('a');
      a.href = fileURL;
      a.target = '_blank';
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
    }
  }

  handleBlobError(err: HttpErrorResponse): Observable<any> {
    const contentType = err.headers?.get('Content-Type');

    if (contentType?.includes('application/json') && err.error instanceof Blob) {
      return from(err.error.text()).pipe(
        switchMap((text: string) => {
          try {
            return [JSON.parse(text)];
          } catch {
            return throwError(() => new Error('Invalid JSON in blob'));
          }
        })
      );
    }

    this.toasterService.showError(MessageConstants.payments.unhandledError, 'full');

    return throwError(() => new Error('Non-JSON error response'));
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Triggers takeUntil
    this.destroy$.complete(); // Final cleanup
  }
}
